import { useCallback, useEffect, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import { useArShareDomain } from "@/configurator/domain/hooks/useArShareDomain";
import { Loc, t } from "@/locales";
import { isDesktopDevice } from "@/configurator/utils/scene/arHelpers";
import type { Scene3D } from "@/configurator/utils/scene/Scene3D";

export type ARStatus = "idle" | "preparing" | "presenting";

const AR_MESSAGES = {
  failed: Loc.arErrorFailed,
  unsupported: Loc.arErrorUnsupported,
  // Browsers only start an AR session from a user gesture, so an auto-start that
  // came from the QR link can be refused; the toolbar button still works.
  needsTap: Loc.arErrorNeedsTap,
} as const;

export type ARPresentation = {
  arStatus: ARStatus;
  arError: string | null;
  // Set while the desktop QR hand-off modal is open.
  shareUrl: string | null;
  openAR: () => void;
  closeShare: () => void;
  onSceneReady: (scene: Scene3D) => void;
};

/**
 * Owns the AR launch flow for the viewer toolbar:
 * - on phones and tablets it drives the renderer's AR session;
 * - on desktops it hands the current configuration to a phone as a QR link;
 * - when opened *from* such a link it restores that configuration and tries to
 *   enter AR right away.
 */
export function useARPresentationComponent(
  sceneRef: MutableRefObject<Scene3D | null>,
): ARPresentation {
  const { buildShareUrl, readShareLink, applySharedSelection } =
    useArShareDomain();
  const [arStatus, setARStatus] = useState<ARStatus>("idle");
  const [arError, setARError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  // Read on the first render, not in an effect: child effects run before parent
  // ones, so the scene reports itself ready before an effect here could arm the
  // auto-start, and the launch would be skipped.
  const shareParamsRef = useRef(readShareLink(window.location.search));
  // The auto-start must fire exactly once, and only once the link's selection is
  // in the store — otherwise AR would open the previous model.
  const autoStartPendingRef = useRef(shareParamsRef.current.autoStartAR);
  const selectionAppliedRef = useRef(false);

  const launchAR = useCallback(
    async (scene: Scene3D, fromAutoStart: boolean) => {
      setARError(null);
      setARStatus("preparing");

      const result = await scene.openAR();
      if (result === "presenting") {
        setARStatus("presenting");
        return;
      }

      setARStatus("idle");
      if (result === "launched") return;

      if (fromAutoStart) {
        setARError(t(AR_MESSAGES.needsTap));
        return;
      }
      setARError(
        t(
          result === "unsupported"
            ? AR_MESSAGES.unsupported
            : AR_MESSAGES.failed,
        ),
      );
    },
    [],
  );

  // Fires the auto-start once both preconditions hold; called from whichever of
  // the two (selection applied, scene ready) happens last.
  const tryAutoStart = useCallback(() => {
    const scene = sceneRef.current;
    if (!autoStartPendingRef.current) return;
    if (!selectionAppliedRef.current || !scene) return;
    if (isDesktopDevice()) return;

    autoStartPendingRef.current = false;
    void launchAR(scene, true);
  }, [launchAR, sceneRef]);

  useEffect(() => {
    const { autoStartAR, selection } = shareParamsRef.current;
    if (!autoStartAR) return;

    applySharedSelection(selection);
    selectionAppliedRef.current = true;
    tryAutoStart();
  }, [applySharedSelection, tryAutoStart]);

  // Read through a ref so `openAR` keeps a stable identity: the builder closes
  // over the current selection and changes on every configuration change, and a
  // toolbar callback that changes with it would churn the whole toolbar.
  const buildShareUrlRef = useRef(buildShareUrl);
  buildShareUrlRef.current = buildShareUrl;

  const openAR = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Desktops cannot present AR at all: offer the phone hand-off instead of
    // failing, and keep the current configuration in the link.
    if (isDesktopDevice()) {
      setARError(null);
      setShareUrl(buildShareUrlRef.current(window.location.href));
      return;
    }

    if (scene.isARPresenting()) {
      void scene.exitAR().then(() => setARStatus("idle"));
      return;
    }

    void launchAR(scene, false);
  }, [launchAR, sceneRef]);

  const closeShare = useCallback(() => setShareUrl(null), []);

  const onSceneReady = useCallback(
    (scene: Scene3D) => {
      sceneRef.current = scene;
      scene.setARSessionEndHandler(() => setARStatus("idle"));
      setARStatus("idle");
      setARError(null);

      tryAutoStart();
    },
    [sceneRef, tryAutoStart],
  );

  return { arStatus, arError, shareUrl, openAR, closeShare, onSceneReady };
}
